/**
 * HorizontalBarChart — a small presentational Recharts horizontal bar chart for
 * "compare a few labelled values" cases: peer usage/bill (You / P25 / Median /
 * P75) and the diagnostics attribution breakdown. Per-datum colour via `color`
 * (e.g. highlight "You"); otherwise a single fallback colour.
 */
import {
  Bar,
  BarChart,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { chartColors } from "@/lib/tokens";

import { tooltipContentStyle } from "./chartTheme";

export interface HBarDatum {
  label: string;
  value: number;
  color?: string;
}

export interface HorizontalBarChartProps {
  data: HBarDatum[];
  /** Format axis ticks + tooltip values (e.g. `v => \`${v} m³\``). */
  valueFormat?: (v: number) => string;
  /** Fallback bar colour when a datum has no `color`. */
  color?: string;
  height?: number | string;
  /** Y-axis label column width (px). */
  labelWidth?: number;
  ariaLabel?: string;
}

export function HorizontalBarChart({
  data,
  valueFormat,
  color,
  height = 220,
  labelWidth = 96,
  ariaLabel,
}: HorizontalBarChartProps) {
  const fallback = color ?? chartColors.brand();
  return (
    <div role="img" aria-label={ariaLabel} className="w-full" style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={data}
          layout="vertical"
          margin={{ top: 4, right: 16, bottom: 0, left: 4 }}
        >
          <XAxis
            type="number"
            tick={{ fontSize: 11, fill: chartColors.fgMuted() }}
            tickLine={false}
            axisLine={{ stroke: chartColors.border() }}
            tickFormatter={valueFormat}
          />
          <YAxis
            type="category"
            dataKey="label"
            width={labelWidth}
            tick={{ fontSize: 11, fill: chartColors.fgMuted() }}
            tickLine={false}
            axisLine={false}
          />
          <Tooltip
            cursor={{ fill: chartColors.border(0.3) }}
            contentStyle={tooltipContentStyle}
            formatter={(v) => (valueFormat ? valueFormat(v as number) : v)}
          />
          <Bar dataKey="value" radius={[0, 4, 4, 0]}>
            {data.map((d, i) => (
              <Cell key={`${d.label}-${i}`} fill={d.color ?? fallback} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
